import StudentRegistrationSettings from "./StudentRegistrationSettings";
import React, { useCallback, useEffect, useRef, useState } from "react";
import CadastroPageHeader from "../../Components/CadastroPageHeader";
import MenuLateral from "../../Components/MenuLateral";
import LocalStorageCapacity from "../../Components/LocalStorageCapacity";
import api from "../../services/api";
import {
  FaClock,
  FaDatabase,
  FaImage,
  FaPaintBrush,
  FaUser,
  FaUserGraduate,
} from "react-icons/fa";
import { FiMenu, FiSettings, FiX } from "react-icons/fi";
import "../../styles/cadastro-pages.css";
import "./index.css";

const MAX_WATERMARK_SIZE_BYTES = 10 * 1024 * 1024;
const MAX_RETENTION_DAYS = 2147483647;
const WATERMARK_MIME_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
]);

const DEFAULT_RECOGNITION_SETTINGS = Object.freeze({
  recognitionStartMode: "ADMIN_APPROVAL",
  eventFaceConfidence: 0.8,
  referenceFaceConfidence: 0.9,
  similarityThreshold: 0.75,
  requestTimeoutMs: 15000,
  maxAttempts: 3,
});

const SYSTEM_TAB = "system";
const IMAGE_POLICY_TAB = "image-policy";
const RECOGNITION_TAB = "recognition";

const emptyPolicy = () => ({
  preserveOriginal: false,
  storageMode: "server",
  unavailableRetentionDays: 0,
  watermark: {
    configured: false,
    mimeType: null,
    version: null,
  },
  version: null,
  cloudAvailable: false,
  maxUploadSizeBytes: 150 * 1024 * 1024,
  localStorageCapacity: null,
});

const normalizeLocalStorageCapacity = (capacity) => {
  if (!capacity?.applicable) return { applicable: false };

  if (capacity.status === "UNAVAILABLE") {
    return { applicable: true, status: "UNAVAILABLE" };
  }

  if (capacity.status !== "OK" && capacity.status !== "LOW") {
    return { applicable: true, status: "UNAVAILABLE" };
  }

  return {
    applicable: true,
    status: capacity.status,
    totalBytes: Number(capacity.totalBytes),
    freeBytes: Number(capacity.freeBytes),
    freePercentage: Number(capacity.freePercentage),
    measuredAt: typeof capacity.measuredAt === "string" ? capacity.measuredAt : "",
  };
};

const normalizePolicy = (data) => {
  const fallback = emptyPolicy();
  const storageMode = data?.storageMode === "cloud" ? "cloud" : "server";
  const retentionDays = Number(data?.unavailableRetentionDays);

  return {
    preserveOriginal: Boolean(data?.preserveOriginal),
    storageMode,
    unavailableRetentionDays:
      Number.isInteger(retentionDays) && retentionDays >= 0
        ? retentionDays
        : fallback.unavailableRetentionDays,
    watermark: {
      configured: Boolean(data?.watermark?.configured),
      mimeType:
        typeof data?.watermark?.mimeType === "string"
          ? data.watermark.mimeType
          : null,
      version: Number.isInteger(data?.watermark?.version)
        ? data.watermark.version
        : null,
    },
    cloudAvailable: Boolean(data?.cloudAvailable),
    maxUploadSizeBytes: Number.isFinite(Number(data?.maxUploadSizeBytes))
      ? Number(data.maxUploadSizeBytes)
      : fallback.maxUploadSizeBytes,
    version: Number.isInteger(data?.version) ? data.version : null,
    localStorageCapacity: normalizeLocalStorageCapacity(
      data?.localStorageCapacity,
    ),
  };
};

const normalizeRecognitionSettings = (data) => ({
  recognitionStartMode:
    data?.recognitionStartMode === "AUTOMATIC_AFTER_PREPARATION"
      ? "AUTOMATIC_AFTER_PREPARATION"
      : "ADMIN_APPROVAL",
  eventFaceConfidence: Number.isFinite(Number(data?.eventFaceConfidence))
    ? Number(data.eventFaceConfidence)
    : DEFAULT_RECOGNITION_SETTINGS.eventFaceConfidence,
  referenceFaceConfidence: Number.isFinite(Number(data?.referenceFaceConfidence))
    ? Number(data.referenceFaceConfidence)
    : DEFAULT_RECOGNITION_SETTINGS.referenceFaceConfidence,
  similarityThreshold: Number.isFinite(Number(data?.similarityThreshold))
    ? Number(data.similarityThreshold)
    : DEFAULT_RECOGNITION_SETTINGS.similarityThreshold,
  requestTimeoutMs: Number.isInteger(Number(data?.requestTimeoutMs))
    ? Number(data.requestTimeoutMs)
    : DEFAULT_RECOGNITION_SETTINGS.requestTimeoutMs,
  maxAttempts: Number.isInteger(Number(data?.maxAttempts))
    ? Number(data.maxAttempts)
    : DEFAULT_RECOGNITION_SETTINGS.maxAttempts,
});

const getErrorMessage = (error, fallback) => {
  if (error?.response?.status === 403) {
    return "Seu perfil nao possui permissao para configurar imagens.";
  }

  const message = error?.response?.data?.message;
  return Array.isArray(message)
    ? message.join(", ")
    : typeof message === "string" && message.trim()
      ? message
      : fallback;
};

const getUploadLimitLabel = (storageMode, effectivePolicy) => {
  const fallbackBytes =
    storageMode === "cloud" ? 500 * 1024 * 1024 : 150 * 1024 * 1024;
  const effectiveBytes = Number(effectivePolicy?.maxUploadSizeBytes);
  const bytes =
    storageMode === effectivePolicy?.storageMode &&
      Number.isFinite(effectiveBytes) &&
      effectiveBytes > 0
      ? effectiveBytes
      : fallbackBytes;

  return `${Math.round(bytes / (1024 * 1024))} MB por arquivo`;
};

function ImageProcessingPolicy() {
  const [policy, setPolicy] = useState(null);
  const [form, setForm] = useState(emptyPolicy);
  const [watermarkFile, setWatermarkFile] = useState(null);
  const [watermarkPreviewUrl, setWatermarkPreviewUrl] = useState("");
  const [replacementConfirmed, setReplacementConfirmed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [formError, setFormError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [policyLocked, setPolicyLocked] = useState(false);
  const [recognitionSettings, setRecognitionSettings] = useState(null);
  const [recognitionForm, setRecognitionForm] = useState(DEFAULT_RECOGNITION_SETTINGS);
  const [recognitionConfirmed, setRecognitionConfirmed] = useState(false);
  const [recognitionLoading, setRecognitionLoading] = useState(true);
  const [recognitionSaving, setRecognitionSaving] = useState(false);
  const [recognitionError, setRecognitionError] = useState("");
  const [recognitionSuccess, setRecognitionSuccess] = useState("");
  const [activeTab, setActiveTab] = useState(SYSTEM_TAB);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const tabRefs = useRef({});
  const requestRef = useRef(0);

  const clearSelectedWatermark = useCallback(() => {
    setWatermarkFile(null);
    setWatermarkPreviewUrl("");
    setReplacementConfirmed(false);
  }, []);

  useEffect(
    () => () => {
      if (watermarkPreviewUrl) {
        window.URL.revokeObjectURL?.(watermarkPreviewUrl);
      }
    },
    [watermarkPreviewUrl],
  );

  const loadPolicy = useCallback(async () => {
    const requestId = ++requestRef.current;

    setLoading(true);
    setLoadError("");
    setFormError("");
    setSuccessMessage("");
    setPolicyLocked(false);

    try {
      const response = await api.get("/image-processing/policy");
      const nextPolicy = normalizePolicy(response.data);

      if (requestRef.current !== requestId) return;

      setPolicy(nextPolicy);
      setForm(nextPolicy);
      clearSelectedWatermark();
    } catch (error) {
      if (requestRef.current !== requestId) return;

      setPolicy(null);
      setLoadError(
        getErrorMessage(error, "Nao foi possivel carregar a configuração de imagens."),
      );
    } finally {
      if (requestRef.current === requestId) {
        setLoading(false);
      }
    }
  }, [clearSelectedWatermark]);

  useEffect(() => {
    loadPolicy();

    return () => {
      requestRef.current += 1;
    };
  }, [loadPolicy]);

  const loadRecognitionSettings = useCallback(async () => {
    setRecognitionLoading(true);
    setRecognitionError("");
    setRecognitionSuccess("");
    try {
      const response = await api.get("/recognition/settings");
      const normalized = normalizeRecognitionSettings(response.data);
      setRecognitionSettings(normalized);
      setRecognitionForm(normalized);
      setRecognitionConfirmed(false);
    } catch (error) {
      setRecognitionSettings(null);
      setRecognitionError(
        getErrorMessage(error, "Nao foi possivel carregar os parametros funcionais de recognition."),
      );
    } finally {
      setRecognitionLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRecognitionSettings();
  }, [loadRecognitionSettings]);

  const updateForm = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setFormError("");
    setSuccessMessage("");
  };

  const handleWatermarkChange = (event) => {
    const nextFile = event.target.files?.[0];
    setFormError("");
    setSuccessMessage("");

    if (!nextFile) {
      clearSelectedWatermark();
      return;
    }

    if (!WATERMARK_MIME_TYPES.has(nextFile.type)) {
      clearSelectedWatermark();
      setFormError("A marca deve ser uma imagem JPEG, PNG ou WebP.");
      event.target.value = "";
      return;
    }

    if (nextFile.size > MAX_WATERMARK_SIZE_BYTES) {
      clearSelectedWatermark();
      setFormError("A marca deve ter no maximo 10 MB.");
      event.target.value = "";
      return;
    }

    setWatermarkFile(nextFile);
    setReplacementConfirmed(false);
    setWatermarkPreviewUrl((currentUrl) => {
      if (currentUrl) {
        window.URL.revokeObjectURL?.(currentUrl);
      }

      return window.URL.createObjectURL?.(nextFile) || "";
    });
  };

  const validateForm = () => {
    const retentionDays = Number(form.unavailableRetentionDays);

    if (
      !Number.isInteger(retentionDays) ||
      retentionDays < 0 ||
      retentionDays > MAX_RETENTION_DAYS
    ) {
      return "Informe um prazo de retencao inteiro, igual ou maior que zero.";
    }

    if (!form.watermark?.configured && !watermarkFile) {
      return "Envie a marca-d'agua interna para concluir o primeiro salvamento.";
    }

    if (form.watermark?.configured && watermarkFile && !replacementConfirmed) {
      return "Confirme a substituicao da marca-d'agua antes de salvar.";
    }

    return "";
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (policyLocked || saving) return;

    const validationError = validateForm();
    if (validationError) {
      setFormError(validationError);
      return;
    }

    setSaving(true);
    setFormError("");
    setSuccessMessage("");

    try {
      const formData = new FormData();
      formData.append("preserveOriginal", String(Boolean(form.preserveOriginal)));
      formData.append("storageMode", form.storageMode);
      formData.append(
        "unavailableRetentionDays",
        String(Number(form.unavailableRetentionDays)),
      );

      if (watermarkFile) {
        formData.append("watermark", watermarkFile);
      }

      const response = await api.put("/image-processing/policy", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const savedPolicy = normalizePolicy(response.data);

      setPolicy(savedPolicy);
      setForm(savedPolicy);
      clearSelectedWatermark();
      setSuccessMessage("Configuração de imagens salva com sucesso.");
    } catch (error) {
      if (error?.response?.status === 409) {
        setPolicyLocked(true);
        setFormError("");
      } else {
        setFormError(
          getErrorMessage(error, "Nao foi possivel salvar a configuração de imagens."),
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const updateRecognitionForm = (field, value) => {
    setRecognitionForm((current) => ({ ...current, [field]: value }));
    setRecognitionError("");
    setRecognitionSuccess("");
    setRecognitionConfirmed(false);
  };

  const getRecognitionChanges = () => {
    if (!recognitionSettings) return {};
    return Object.entries(recognitionForm).reduce((changes, [field, value]) => {
      if (field === "recognitionStartMode") {
        if (value !== recognitionSettings[field]) changes[field] = value;
        return changes;
      }
      const numericValue = Number(value);
      if (numericValue !== Number(recognitionSettings[field])) {
        changes[field] = numericValue;
      }
      return changes;
    }, {});
  };

  const validateRecognitionSettings = (changes) => {
    if (Object.keys(changes).length === 0) {
      return "Altere ao menos um parametro antes de salvar.";
    }
    if (
      ["eventFaceConfidence", "referenceFaceConfidence", "similarityThreshold"].some(
        (field) => !Number.isFinite(Number(recognitionForm[field])) || Number(recognitionForm[field]) < 0 || Number(recognitionForm[field]) > 1,
      )
    ) {
      return "As confiancas e a similaridade devem estar entre 0 e 1.";
    }
    if (
      !Number.isInteger(Number(recognitionForm.requestTimeoutMs)) ||
      Number(recognitionForm.requestTimeoutMs) < 1000 ||
      Number(recognitionForm.requestTimeoutMs) > 120000
    ) {
      return "O timeout deve ser um inteiro entre 1000 e 120000 ms.";
    }
    if (!Number.isInteger(Number(recognitionForm.maxAttempts)) || Number(recognitionForm.maxAttempts) < 1 || Number(recognitionForm.maxAttempts) > 3) {
      return "As tentativas devem ser um inteiro entre 1 e 3.";
    }
    if (!recognitionConfirmed) {
      return "Confirme que a alteracao vale somente para novos processamentos e reprocessamentos.";
    }
    return "";
  };

  const handleRecognitionSettingsSubmit = async () => {
    if (recognitionSaving || recognitionLoading) return;
    const changes = getRecognitionChanges();
    const validationError = validateRecognitionSettings(changes);
    if (validationError) {
      setRecognitionError(validationError);
      return;
    }

    setRecognitionSaving(true);
    setRecognitionError("");
    setRecognitionSuccess("");
    try {
      const response = await api.put("/recognition/settings", {
        ...changes,
      });
      const normalized = normalizeRecognitionSettings(response.data);
      setRecognitionSettings(normalized);
      setRecognitionForm(normalized);
      setRecognitionConfirmed(false);
      setRecognitionSuccess("Parametros de recognition salvos para novos processamentos.");
    } catch (error) {
      setRecognitionError(
        getErrorMessage(error, "Nao foi possivel salvar os parametros de recognition."),
      );
    } finally {
      setRecognitionSaving(false);
    }
  };

  const isDisabled = loading || saving || policyLocked;
  const firstSave = policy && !form.watermark?.configured;
  const tabs = [SYSTEM_TAB, IMAGE_POLICY_TAB, RECOGNITION_TAB];

  const handleTabKeyDown = (event) => {
    const currentIndex = tabs.indexOf(activeTab);
    let nextIndex = currentIndex;

    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      nextIndex = (currentIndex + 1) % tabs.length;
    } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    } else if (event.key === "Home") {
      nextIndex = 0;
    } else if (event.key === "End") {
      nextIndex = tabs.length - 1;
    } else {
      return;
    }

    event.preventDefault();
    const nextTab = tabs[nextIndex];
    setActiveTab(nextTab);
    tabRefs.current[nextTab]?.focus();
  };

  return (
    <div className="pagina-image-policy cadastro-shell">
      <button
        className="image-policy-mobile-menu-toggle"
        type="button"
        aria-controls="image-policy-navigation"
        aria-expanded={mobileMenuOpen}
        onClick={() => setMobileMenuOpen((open) => !open)}
      >
        <span className="image-policy-mobile-brand">FORMARI</span>
        <span className="image-policy-mobile-menu-action">
          {mobileMenuOpen ? <FiX aria-hidden="true" /> : <FiMenu aria-hidden="true" />}
          {mobileMenuOpen ? "Fechar menu" : "Abrir menu"}
        </span>
      </button>
      <div
        id="image-policy-navigation"
        className={`image-policy-menu-container${mobileMenuOpen ? " is-open" : ""}`}
      >
        <MenuLateral />
      </div>

      <main className="conteudo-image-policy">
        <CadastroPageHeader
          eyebrow="Políticas e configurações"
          title="Configurações"
          description="Gerencie as regras de cadastro, imagens e reconhecimento da sua empresa."
          icon={FiSettings}
          context="Ajustes da empresa"
          contextDetail="Revise cada área antes de salvar"
        />

        <div className="image-policy-form">
            <div className="image-policy-tabs">
              <div className="image-policy-tablist" role="tablist" aria-label="Categorias de configuração">
                <button
                  ref={(element) => {
                    tabRefs.current[SYSTEM_TAB] = element;
                  }}
                  className="image-policy-tab"
                  id="system-tab"
                  type="button"
                  role="tab"
                  aria-selected={activeTab === SYSTEM_TAB}
                  aria-controls="system-panel"
                  tabIndex={activeTab === SYSTEM_TAB ? 0 : -1}
                  onClick={() => setActiveTab(SYSTEM_TAB)}
                  onKeyDown={handleTabKeyDown}
                >
                  <FaUserGraduate className="image-policy-tab-icon" aria-hidden="true" />
                  <span className="image-policy-tab-copy">
                    <span className="image-policy-tab-kicker">CADASTROS</span>
                    <span className="image-policy-tab-label">Configurações do sistema</span>
                    <span className="image-policy-tab-description">Aprovação de alunos por link</span>
                  </span>
                </button>
                <button
                  ref={(element) => {
                    tabRefs.current[IMAGE_POLICY_TAB] = element;
                  }}
                  className="image-policy-tab"
                  id="image-policy-tab"
                  type="button"
                  role="tab"
                  aria-selected={activeTab === IMAGE_POLICY_TAB}
                  aria-controls="image-policy-panel"
                  tabIndex={activeTab === IMAGE_POLICY_TAB ? 0 : -1}
                  onClick={() => setActiveTab(IMAGE_POLICY_TAB)}
                  onKeyDown={handleTabKeyDown}
                >
                  <FaImage className="image-policy-tab-icon" aria-hidden="true" />
                  <span className="image-policy-tab-copy">
                    <span className="image-policy-tab-kicker">POLÍTICA</span>
                    <span className="image-policy-tab-label">Imagens</span>
                    <span className="image-policy-tab-description">Armazenamento, retenção e marca</span>
                  </span>
                </button>
                <button
                  ref={(element) => {
                    tabRefs.current[RECOGNITION_TAB] = element;
                  }}
                  className="image-policy-tab"
                  id="recognition-tab"
                  type="button"
                  role="tab"
                  aria-selected={activeTab === RECOGNITION_TAB}
                  aria-controls="recognition-panel"
                  tabIndex={activeTab === RECOGNITION_TAB ? 0 : -1}
                  onClick={() => setActiveTab(RECOGNITION_TAB)}
                  onKeyDown={handleTabKeyDown}
                >
                  <FaUser className="image-policy-tab-icon" aria-hidden="true" />
                  <span className="image-policy-tab-copy">
                    <span className="image-policy-tab-kicker">RECONHECIMENTO</span>
                    <span className="image-policy-tab-label">Reconhecimento Facial</span>
                    <span className="image-policy-tab-description">Filtros e tentativas locais</span>
                  </span>
                </button>
              </div>

              <section
                className="image-policy-tabpanel image-policy-system-panel"
                id="system-panel"
                role="tabpanel"
                aria-labelledby="system-tab"
                hidden={activeTab !== SYSTEM_TAB}
              >
                <StudentRegistrationSettings />
              </section>

              {activeTab === IMAGE_POLICY_TAB ? (
                <section
                  className="image-policy-tabpanel"
                  id="image-policy-panel"
                  role="tabpanel"
                  aria-labelledby="image-policy-tab"
                >
                  {loading ? (
                    <div className="image-policy-state" role="status" aria-busy="true">
                      Carregando configuração de imagens...
                    </div>
                  ) : null}
                  {loadError ? (
                    <section className="image-policy-state error" role="alert">
                      <p>{loadError}</p>
                      <button type="button" onClick={loadPolicy}>Tentar novamente</button>
                    </section>
                  ) : null}
                  {policy && !loading ? (
                    <div className="image-policy-panel-content">
                      {policy.storageMode === "server" && policy.localStorageCapacity?.applicable ? (
                        <div className="image-policy-capacity-top">
                          <span className="image-policy-eyebrow">VISÃO OPERACIONAL</span>
                          <LocalStorageCapacity capacity={policy.localStorageCapacity} />
                        </div>
                      ) : null}
                  <section className="image-policy-saved-summary" aria-labelledby="image-policy-summary-title">
                    <div className="image-policy-saved-summary-intro">
                      <span className="image-policy-eyebrow">ESTADO SALVO</span>
                      <h2 id="image-policy-summary-title">O que será aplicado aos próximos envios</h2>
                      <p>
                        Confira o resumo antes de salvar. As alterações não mudam fotos que já foram processadas.
                      </p>
                    </div>
                    <dl className="image-policy-saved-summary-list">
                      <div>
                        <dt>Armazenamento</dt>
                        <dd>{policy.storageMode === "cloud" ? "Nuvem" : "Servidor"}</dd>
                      </div>
                      <div>
                        <dt>Original</dt>
                        <dd>{policy.preserveOriginal ? "Preservado" : "Fonte interna"}</dd>
                      </div>
                      <div>
                        <dt>Retenção</dt>
                        <dd>
                          {policy.unavailableRetentionDays === 0
                            ? "Sem limpeza"
                            : `${policy.unavailableRetentionDays} dias`}
                        </dd>
                      </div>
                      <div>
                        <dt>Marca interna</dt>
                        <dd>{policy.watermark?.configured ? "Configurada" : "Pendente"}</dd>
                      </div>
                    </dl>
                  </section>

                  <form className="image-policy-panel-form" onSubmit={handleSubmit} noValidate>
                    <section className="image-policy-section">
                      <h2>
                        <FaImage className="image-policy-section-icon" aria-hidden="true" focusable="false" />
                        <span>Preservacao do original</span>
                      </h2>
                      <label className="image-policy-checkbox">
                        <input
                          type="checkbox"
                          checked={Boolean(form.preserveOriginal)}
                          onChange={(event) =>
                            updateForm("preserveOriginal", event.target.checked)
                          }
                          disabled={isDisabled}
                        />
                        Preservar a resolucao original apos o preparo interno
                      </label>
                      <p className="image-policy-help">
                        Desmarcado, o original nao e mantido depois de gerar a fonte interna de reconhecimento.
                      </p>
                    </section>

                    <section className="image-policy-section">
                      <h2>
                        <FaClock className="image-policy-section-icon" aria-hidden="true" focusable="false" />
                        <span>Prazo de retencao de fotos indisponiveis</span>
                      </h2>
                      <label htmlFor="unavailableRetentionDays">
                        Dias
                        <input
                          id="unavailableRetentionDays"
                          name="unavailableRetentionDays"
                          type="number"
                          min="0"
                          max={MAX_RETENTION_DAYS}
                          step="1"
                          value={form.unavailableRetentionDays}
                          onChange={(event) =>
                            updateForm("unavailableRetentionDays", event.target.value)
                          }
                          disabled={isDisabled}
                        />
                      </label>
                      <p className="image-policy-help">
                        Use 0 para desabilitar a limpeza automatica. Historicos e vendas que impedem exclusao continuam protegidos.
                      </p>
                    </section>

                    <section className="image-policy-section">
                      <h2>
                        <FaDatabase className="image-policy-section-icon" aria-hidden="true" focusable="false" />
                        <span>Armazenamento de fotos</span>
                      </h2>
                      <div className="image-policy-options image-policy-storage-options" role="radiogroup" aria-label="Armazenamento de fotos">
                        <label>
                          <input
                            type="radio"
                            name="storageMode"
                            value="server"
                            checked={form.storageMode === "server"}
                            onChange={(event) => updateForm("storageMode", event.target.value)}
                            disabled={isDisabled}
                          />
                          <span className="image-policy-option-copy">
                            <strong>Servidor</strong>
                            <span className="image-policy-option-limit" title="Limite por arquivo no servidor">
                              Limite: 150 MB por arquivo.
                            </span>
                          </span>
                        </label>
                        <label className={!policy.cloudAvailable ? "disabled-option" : ""}>
                          <input
                            type="radio"
                            name="storageMode"
                            value="cloud"
                            checked={form.storageMode === "cloud"}
                            onChange={(event) => updateForm("storageMode", event.target.value)}
                            disabled={isDisabled || !policy.cloudAvailable}
                          />
                          <span className="image-policy-option-copy">
                            <strong>Nuvem</strong>
                            <span className="image-policy-option-limit" title="Limite por arquivo na nuvem">
                              Limite: 500 MB por arquivo.
                            </span>
                            {!policy.cloudAvailable ? (
                              <span className="image-policy-option-disabled">Indisponivel neste ambiente.</span>
                            ) : null}
                          </span>
                        </label>
                      </div>
                      <p className="image-policy-help">
                        Limite previsto para o modo selecionado: {getUploadLimitLabel(form.storageMode, policy)}. A alteracao vale somente para novos envios e reenvios.
                      </p>
                    </section>

                    <section className="image-policy-section">
                      <h2>
                        <FaPaintBrush className="image-policy-section-icon" aria-hidden="true" focusable="false" />
                        <span>Marca-d'agua interna</span>
                      </h2>
                      {firstSave ? (
                        <p className="image-policy-required" role="status">
                          Nenhuma politica foi salva ainda. Envie uma marca para concluir a configuração inicial.
                        </p>
                      ) : (
                        <p className="image-policy-status">
                          Uma marca interna esta configurada. Voce pode manter a atual ou selecionar outra para substitui-la.
                        </p>
                      )}

                      <label htmlFor="watermark">
                        {firstSave ? "Enviar marca obrigatoria" : "Substituir marca (opcional)"}
                        <input
                          id="watermark"
                          name="watermark"
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          onChange={handleWatermarkChange}
                          disabled={isDisabled}
                        />
                      </label>
                      <p className="image-policy-help">
                        Aceita JPEG, PNG ou WebP de ate 10 MB. A imagem fica interna e nao e exibida por URL publica.
                      </p>

                      {watermarkFile ? (
                        <div className="watermark-preview">
                          <strong>Pre-visualizacao local da nova marca</strong>
                          <span>{watermarkFile.name}</span>
                          {watermarkPreviewUrl ? (
                            <img
                              src={watermarkPreviewUrl}
                              alt="Pre-visualizacao local da nova marca"
                            />
                          ) : null}
                        </div>
                      ) : null}

                      {form.watermark?.configured && watermarkFile ? (
                        <label className="image-policy-checkbox replacement-confirmation">
                          <input
                            type="checkbox"
                            checked={replacementConfirmed}
                            onChange={(event) => setReplacementConfirmed(event.target.checked)}
                            disabled={isDisabled}
                          />
                          Confirmo a substituicao da marca-d'agua interna atual.
                        </label>
                      ) : null}
                    </section>

                    {policyLocked ? (
                      <section className="image-policy-state warning" role="alert">
                        <p>
                          Ha arquivos em processamento. A politica so podera ser alterada apos o processamento.
                        </p>
                        <button type="button" onClick={loadPolicy}>
                          Atualizar status
                        </button>
                      </section>
                    ) : null}

                    {formError ? (
                      <div className="image-policy-feedback error" role="alert">
                        {formError}
                      </div>
                    ) : null}
                    {successMessage ? (
                      <div className="image-policy-feedback success" role="status">
                        {successMessage}
                      </div>
                    ) : null}

                    <div className="image-policy-actions">
                      <button type="submit" disabled={isDisabled}>
                        {saving ? "Salvando..." : "Salvar configuração"}
                      </button>
                      <button type="button" className="secondary" onClick={loadPolicy} disabled={saving}>
                        Descartar alteracoes
                      </button>
                    </div>
                  </form>
                    </div>
                  ) : null}
                </section>
              ) : activeTab === RECOGNITION_TAB ? (
                <section
                  className="image-policy-tabpanel"
                  id="recognition-panel"
                  role="tabpanel"
                  aria-labelledby="recognition-tab"
                >
                  <section className="image-policy-section recognition-settings-section">
                    <h2>
                      <FaUser className="image-policy-section-icon" aria-hidden="true" focusable="false" />
                      <span>Reconhecimento Facial</span>
                    </h2>
                    <p className="image-policy-help">
                      Parametros funcionais provisorios da sua empresa. Eles filtram os resultados recebidos pelo Formari e nao exibem endpoint, chave ou outra configuração de infraestrutura.
                    </p>
                    <p className="image-policy-help">
                      Reduzir a confianca local nao recupera rostos que o detector do CompreFace nao retornou. A calibracao posterior nao bloqueia upload ou recognition nesta fase.
                    </p>

                    {recognitionLoading ? (
                      <div className="image-policy-state" role="status">Carregando parametros de recognition...</div>
                    ) : null}
                    {recognitionError ? (
                      <div className="image-policy-feedback error" role="alert">{recognitionError}</div>
                    ) : null}

                    {recognitionSettings && !recognitionLoading ? (
                      <div className="image-policy-options recognition-settings-grid">
                        <label htmlFor="eventFaceConfidence">
                          Confianca de rosto no evento
                          <input
                            id="eventFaceConfidence"
                            type="number"
                            min="0"
                            max="1"
                            step="0.01"
                            value={recognitionForm.eventFaceConfidence}
                            onChange={(event) => updateRecognitionForm("eventFaceConfidence", event.target.value)}
                            disabled={recognitionSaving}
                          />
                        </label>
                        <label htmlFor="recognitionStartMode">
                          Início do reconhecimento
                          <select
                            id="recognitionStartMode"
                            value={recognitionForm.recognitionStartMode}
                            onChange={(event) => updateRecognitionForm("recognitionStartMode", event.target.value)}
                            disabled={recognitionSaving}
                          >
                            <option value="ADMIN_APPROVAL">Após aprovação administrativa</option>
                            <option value="AUTOMATIC_AFTER_PREPARATION">Automaticamente após upload</option>
                          </select>
                        </label>
                        <label htmlFor="referenceFaceConfidence">
                          Confianca da referencia
                          <input
                            id="referenceFaceConfidence"
                            type="number"
                            min="0"
                            max="1"
                            step="0.01"
                            value={recognitionForm.referenceFaceConfidence}
                            onChange={(event) => updateRecognitionForm("referenceFaceConfidence", event.target.value)}
                            disabled={recognitionSaving}
                          />
                        </label>
                        <label htmlFor="similarityThreshold">
                          Similaridade minima
                          <input
                            id="similarityThreshold"
                            type="number"
                            min="0"
                            max="1"
                            step="0.01"
                            value={recognitionForm.similarityThreshold}
                            onChange={(event) => updateRecognitionForm("similarityThreshold", event.target.value)}
                            disabled={recognitionSaving}
                          />
                        </label>
                        <label htmlFor="requestTimeoutMs">
                          Timeout por foto (ms)
                          <input
                            id="requestTimeoutMs"
                            type="number"
                            min="1000"
                            max="120000"
                            step="1000"
                            value={recognitionForm.requestTimeoutMs}
                            onChange={(event) => updateRecognitionForm("requestTimeoutMs", event.target.value)}
                            disabled={recognitionSaving}
                          />
                        </label>
                        <label htmlFor="maxAttempts">
                          Tentativas automaticas
                          <input
                            id="maxAttempts"
                            type="number"
                            min="1"
                            max="3"
                            step="1"
                            value={recognitionForm.maxAttempts}
                            onChange={(event) => updateRecognitionForm("maxAttempts", event.target.value)}
                            disabled={recognitionSaving}
                          />
                        </label>
                      </div>
                    ) : null}

                    {recognitionSettings && !recognitionLoading ? (
                      <>
                        <label className="image-policy-checkbox replacement-confirmation">
                          <input
                            type="checkbox"
                            checked={recognitionConfirmed}
                            onChange={(event) => setRecognitionConfirmed(event.target.checked)}
                            disabled={recognitionSaving}
                          />
                          Confirmo que a alteração vale somente para fotos cujo preparo terminar depois do salvamento e para novos reprocessamentos.
                        </label>
                        <div className="image-policy-actions">
                          <button type="button" onClick={handleRecognitionSettingsSubmit} disabled={recognitionSaving}>
                            {recognitionSaving ? "Salvando parametros..." : "Salvar parâmetros"}
                          </button>
                          <button type="button" className="secondary" onClick={loadRecognitionSettings} disabled={recognitionSaving}>
                            Descartar parametros
                          </button>
                        </div>
                      </>
                    ) : null}

                    {recognitionSuccess ? <div className="image-policy-feedback success" role="status">{recognitionSuccess}</div> : null}
                  </section>
                </section>
              ) : null}
            </div>
          </div>
      </main>
    </div>
  );
}

export default ImageProcessingPolicy;
